// src/pages/FinancePage/components/funnel-detail/UnitTable.tsx

import { useState, useEffect } from 'react';
import type { ComponentType } from 'react';
import { Pencil, Target, TrendingUp, WalletCards } from 'lucide-react';
import { UnitConceptRow, UnitConceptId, Severity } from './types';
import { EditableTargetCell } from './EditableTargetCell';
import { CpaModal } from './CpaModal';
import {
  formatCurrency, formatCurrencySigned, formatPercentage,
  getSeverity, brechaTextColor,
  calculateUnitValues,
} from './utils';

type Props = {
  rows: UnitConceptRow[];
  onTargetChange: (id: UnitConceptId, value: number) => void;
  expectedProfitPct: number;
  onExpectedProfitChange: (v: number) => void;
};

export function UnitTable({ rows, onTargetChange, expectedProfitPct, onExpectedProfitChange }: Props) {
  const [editingProfit, setEditingProfit] = useState(false);
  const [profitDraft, setProfitDraft] = useState(expectedProfitPct.toString());
  const [cpaModalOpen, setCpaModalOpen] = useState(false);

  useEffect(() => { if (!editingProfit) setProfitDraft(expectedProfitPct.toString()); }, [expectedProfitPct, editingProfit]);

  const productCostRow = rows.find(r => r.id === 'unit-product-cost')!;
  const shippingCostRow = rows.find(r => r.id === 'unit-shipping-cost')!;
  const returnsRateRow = rows.find(r => r.id === 'unit-returns-pct')!;
  const cpaRow = rows.find(r => r.id === 'unit-cpa')!;
  const salePriceRow = rows.find(r => r.id === 'unit-sale-price')!;

  const realDerived = calculateUnitValues(rows, false);
  const targetDerived = calculateUnitValues(rows, true, expectedProfitPct);

  const realPrice = salePriceRow.realValue ?? 0;
  const targetPrice = targetDerived.effectivePrice;

  const pctOf = (value: number | null, base: number) => value !== null && base > 0 ? (value / base) * 100 : 0;

  const commitProfit = () => {
    const parsed = parseFloat(profitDraft);
    if (!isNaN(parsed) && parsed > 0 && parsed < 100) onExpectedProfitChange(parsed);
    setEditingProfit(false);
  };

  return (
    <>
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white tabular-nums">
        <header className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Calculadora del producto</h2>
            <p className="text-sm text-gray-500 mt-0.5">Compara el precio real de las órdenes recaudadas contra el costo unitario, envío/flete, devoluciones y CPA objetivo.</p>
          </div>

          <div className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 text-sm font-semibold text-[#2b5ce6]">
            <Target className="h-4 w-4" />
            <span>Margen objetivo</span>
            {editingProfit ? (
              <input
                autoFocus
                type="text"
                value={profitDraft}
                onChange={(e) => setProfitDraft(e.target.value)}
                onBlur={commitProfit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitProfit();
                  if (e.key === 'Escape') setEditingProfit(false);
                }}
                className="h-8 w-16 rounded-lg border border-blue-200 bg-white px-2 text-center text-sm font-semibold tabular-nums text-gray-900 outline-none focus:border-[#2b5ce6]"
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditingProfit(true)}
                className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-sm font-bold text-gray-900 transition hover:bg-blue-100"
              >
                <span>{expectedProfitPct}<span className="text-[#2b5ce6]">%</span></span>
                <Pencil className="h-3 w-3 text-[#2b5ce6]" />
              </button>
            )}
          </div>
        </header>

        <div className="grid gap-3 border-b border-gray-100 px-5 py-4 md:grid-cols-3">
          <MetricTile
            icon={WalletCards}
            label="Precio real"
            value={formatCurrency(realPrice)}
            detail="Promedio de órdenes recaudadas"
          />
          <MetricTile
            icon={TrendingUp}
            label="Utilidad real"
            value={formatCurrencySigned(realDerived.finalProfit)}
            detail={`${formatPercentage(realDerived.finalProfitPct)} por orden`}
            tone={realDerived.finalProfit >= 0 ? "good" : "bad"}
          />
          <MetricTile
            icon={Target}
            label="Precio objetivo"
            value={formatCurrency(targetPrice)}
            detail={`${formatPercentage(expectedProfitPct)} margen esperado`}
          />
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Concepto</th>
              <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Valor real</th>
              <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Objetivo</th>
              <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold w-32">Diferencia</th>
            </tr>
          </thead>
          <tbody>
            <CostRow
              row={productCostRow}
              realPrice={realPrice}
              targetPrice={targetPrice}
              onTargetChange={onTargetChange}
            />
            <CostRow
              row={shippingCostRow}
              realPrice={realPrice}
              targetPrice={targetPrice}
              onTargetChange={onTargetChange}
            />
            <RateBasedEditableRow
              label="Devoluciones"
              rateRow={returnsRateRow}
              realComputed={realDerived.returnsAmount}
              targetComputed={targetDerived.returnsAmount}
              onTargetChange={onTargetChange}
            />

            <HighlightRow
              label="Costo total antes de pauta"
              colorScheme="red"
              realValue={realDerived.totalCosts}
              targetValue={targetDerived.totalCosts}
              realPct={pctOf(realDerived.totalCosts, realPrice)}
              targetPct={pctOf(targetDerived.totalCosts, targetPrice)}
              severity={getSeverity(realDerived.totalCosts, targetDerived.totalCosts, true)}
            />

            <CpaRow
              row={cpaRow}
              realPrice={realPrice}
              targetPrice={targetPrice}
              onOpenModal={() => setCpaModalOpen(true)}
            />

            <PriceRow realValue={realPrice} targetValue={targetPrice} />

            <ProfitBannerRow
              realValue={realDerived.finalProfit}
              targetValue={targetDerived.finalProfit}
              realPct={realDerived.finalProfitPct}
              targetPct={expectedProfitPct}
            />
          </tbody>
        </table>
      </section>

      {cpaModalOpen && (
        <CpaModal
          currentCpa={cpaRow.realValue ?? 0}
          targetCpa={cpaRow.targetValue}
          maxProfitableCpa={targetPrice - targetDerived.totalCosts}
          effectiveTargetPrice={targetPrice}
          onTargetChange={(v) => onTargetChange('unit-cpa', v)}
          onClose={() => setCpaModalOpen(false)}
        />
      )}
    </>
  );
}

// === Helpers de render por tipo de fila ===

function MetricTile({
  icon: Icon,
  label,
  value,
  detail,
  tone = "neutral",
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  detail: string;
  tone?: "neutral" | "good" | "bad";
}) {
  const valueClass = tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-red-700" : "text-gray-900";

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/60 px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase text-gray-500">{label}</p>
        <Icon className="h-4 w-4 text-gray-400" />
      </div>
      <p className={`mt-2 text-xl font-bold ${valueClass}`}>{value}</p>
      <p className="mt-1 text-xs text-gray-500">{detail}</p>
    </div>
  );
}

function CostRow({ row, realPrice, targetPrice, onTargetChange }: {
  row: UnitConceptRow;
  realPrice: number;
  targetPrice: number;
  onTargetChange: (id: UnitConceptId, v: number) => void;
}) {
  const editable = row.kind === 'input';
  const realValue = row.realValue;
  const targetValue = editable ? row.targetValue : (row.realValue ?? 0);
  const realPct = realValue !== null && realPrice > 0 ? (realValue / realPrice) * 100 : null;
  const targetPct = targetPrice > 0 ? (targetValue / targetPrice) * 100 : null;
  const gap = realValue !== null ? realValue - targetValue : null;
  const severity = editable ? getSeverity(realValue, targetValue, true) : 'no-data';

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50/40">
      <td className="px-4 py-2.5 text-gray-700">{row.label}</td>
      <td className="text-right px-3 py-2.5">
        <div className="text-gray-700">{formatCurrency(realValue)}</div>
        <div className="text-[11px] text-gray-400">{formatPercentage(realPct)}</div>
      </td>
      <td className="text-right px-3 py-2.5">
        <div>
          <EditableTargetCell
            value={targetValue}
            unit={row.unit}
            editable={editable}
            onChange={(v) => onTargetChange(row.id, v)}
          />
        </div>
        <div className="text-[11px] text-gray-300">{formatPercentage(targetPct)}</div>
      </td>
      <td className={`text-right px-3 py-2.5 ${brechaTextColor(severity)}`}>
        {gap !== null && Math.abs(gap) >= 1 ? formatCurrencySigned(gap) : '—'}
      </td>
    </tr>
  );
}

// Devoluciones: tasa editable, monto se calcula
function RateBasedEditableRow({ label, rateRow, realComputed, targetComputed, onTargetChange }: {
  label: string;
  rateRow: UnitConceptRow;
  realComputed: number;
  targetComputed: number;
  onTargetChange: (id: UnitConceptId, v: number) => void;
}) {
  const rateRealValue = rateRow.realValue ?? 0;
  const ppGap = rateRealValue - rateRow.targetValue;
  const severity = getSeverity(realComputed, targetComputed, true);

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50/40">
      <td className="px-4 py-2.5 text-gray-700">{label}</td>
      <td className="text-right px-3 py-2.5">
        <div className="text-gray-700">{formatCurrency(realComputed)}</div>
        <div className="text-[11px] text-gray-400">{formatPercentage(rateRow.realValue)}</div>
      </td>
      <td className="text-right px-3 py-2.5">
        <div className="text-gray-500 italic">{formatCurrency(targetComputed)}</div>
        <div className="text-[11px]">
          <EditableTargetCell value={rateRow.targetValue} unit="percentage" editable onChange={(v) => onTargetChange(rateRow.id, v)} />
        </div>
      </td>
      <td className={`text-right px-3 py-2.5 ${brechaTextColor(severity)}`}>
        {Math.abs(ppGap) >= 0.05
          ? `${ppGap > 0 ? '+' : '−'}${Math.abs(ppGap).toFixed(1)}%`
          : '—'}
      </td>
    </tr>
  );
}

function HighlightRow({ label, colorScheme, realValue, targetValue, realPct, targetPct, severity }: {
  label: string;
  colorScheme: 'red' | 'green' | 'blue';
  realValue: number;
  targetValue: number;
  realPct: number;
  targetPct: number;
  severity: Severity;
}) {
  const styles = {
    red: { bg: 'bg-red-50', text: 'text-red-900', border: 'border-red-100' },
    green: { bg: 'bg-emerald-50', text: 'text-emerald-900', border: 'border-emerald-100' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-900', border: 'border-blue-100' },
  }[colorScheme];

  const ppGap = realPct - targetPct;

  return (
    <tr className={`border-t-2 ${styles.border} ${styles.bg}`}>
      <td className="px-4 py-3">
        <div className={`font-semibold ${styles.text}`}>{label}</div>
      </td>
      <td className="text-right px-3 py-3">
        <div className={`font-semibold ${styles.text}`}>{formatCurrency(realValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(realPct)}</div>
      </td>
      <td className="text-right px-3 py-3">
        <div className={`font-semibold ${styles.text}`}>{formatCurrency(targetValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(targetPct)}</div>
      </td>
      <td className={`text-right px-3 py-3 font-medium ${brechaTextColor(severity)}`}>
        {Math.abs(ppGap) >= 0.05
          ? `${ppGap > 0 ? '+' : '−'}${Math.abs(ppGap).toFixed(1)}%`
          : '—'}
      </td>
    </tr>
  );
}

function CpaRow({ row, realPrice, targetPrice, onOpenModal }: {
  row: UnitConceptRow;
  realPrice: number;
  targetPrice: number;
  onOpenModal: () => void;
}) {
  const realValue = row.realValue ?? 0;
  const realPct = realPrice > 0 ? (realValue / realPrice) * 100 : 0;
  const targetPct = targetPrice > 0 ? (row.targetValue / targetPrice) * 100 : 0;
  const ppGap = realPct - targetPct;
  const severity = getSeverity(row.realValue, row.targetValue, true);

  return (
    <tr className="border-t border-gray-100 bg-white hover:bg-gray-50/50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#2b5ce6]" />
          <span className="font-semibold text-gray-900">CPA</span>
        </div>
        <div className="mt-0.5 text-[11px] text-gray-500">Pauta promedio necesaria por compra</div>
      </td>
      <td className="text-right px-3 py-3">
        <div className="font-semibold text-gray-800">{formatCurrency(row.realValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(realPct)}</div>
      </td>
      <td className="text-right px-3 py-3">
        <button
          type="button"
          onClick={onOpenModal}
          className="group -mx-2 -my-1 inline-flex flex-col items-end rounded-lg border border-transparent px-2 py-1 transition hover:border-blue-100 hover:bg-blue-50"
        >
          <div className="font-semibold text-[#2b5ce6] inline-flex items-center gap-1.5">
            {formatCurrency(row.targetValue)}
            <Pencil className="w-3 h-3 text-blue-400 group-hover:text-blue-700" />
          </div>
          <div className="text-[11px] text-gray-500">{formatPercentage(targetPct)}</div>
        </button>
      </td>
      <td className={`text-right px-3 py-3 font-medium ${brechaTextColor(severity)}`}>
        {Math.abs(ppGap) >= 0.05
          ? `${ppGap > 0 ? '+' : '−'}${Math.abs(ppGap).toFixed(1)}%`
          : '—'}
      </td>
    </tr>
  );
}

function PriceRow({ realValue, targetValue }: { realValue: number; targetValue: number }) {
  const gap = realValue - targetValue;
  const severity = getSeverity(realValue, targetValue, false);

  return (
    <tr className="border-t-2 border-emerald-100 bg-emerald-50">
      <td className="px-4 py-3">
        <div className="font-semibold text-emerald-900">Precio de venta real</div>
        <div className="text-[10px] text-emerald-700/80 mt-0.5">promedio de las órdenes recaudadas; el objetivo sí se calcula</div>
      </td>
      <td className="text-right px-3 py-3">
        <div className="font-semibold text-emerald-900">{formatCurrency(realValue)}</div>
        <div className="text-[11px] text-gray-500">100.0%</div>
      </td>
      <td className="text-right px-3 py-3">
        <div className="font-semibold text-emerald-900 italic">{formatCurrency(targetValue)}</div>
        <div className="text-[11px] text-gray-500">100.0%</div>
      </td>
      <td className={`text-right px-3 py-3 font-medium ${brechaTextColor(severity)}`}>
        {gap !== 0 ? formatCurrencySigned(gap) : '—'}
      </td>
    </tr>
  );
}

function ProfitBannerRow({ realValue, targetValue, realPct, targetPct }: {
  realValue: number;
  targetValue: number;
  realPct: number;
  targetPct: number;
}) {
  const ppGap = realPct - targetPct;
  const severity = getSeverity(realValue, targetValue, false);
  const rowClass = realValue < 0 ? "bg-red-50/80 border-red-100" : "bg-emerald-50/70 border-emerald-100";
  const valueClass = realValue < 0 ? "text-red-800" : "text-emerald-800";

  return (
    <tr className={`border-t-2 ${rowClass}`}>
      <td className="px-4 py-4">
        <div className="font-bold text-gray-900 text-base">Utilidad por orden</div>
      </td>
      <td className="text-right px-3 py-4">
        <div className={`font-bold text-base ${valueClass}`}>{formatCurrency(realValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(realPct)}</div>
      </td>
      <td className="text-right px-3 py-4">
        <div className="font-bold text-base text-gray-900">{formatCurrency(targetValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(targetPct)}</div>
      </td>
      <td className="text-right px-3 py-4">
        <div className={`font-semibold ${brechaTextColor(severity)}`}>
          {Math.abs(ppGap) >= 0.05
            ? `${ppGap > 0 ? '+' : '−'}${Math.abs(ppGap).toFixed(1)}%`
            : '—'}
        </div>
      </td>
    </tr>
  );
}
